// @flow

import * as React from 'react';
import classnames from 'classnames';
import {defineMessages, FormattedMessage} from 'react-intl';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {BodyAlt} from 'sail/Text';
import Icon from 'sail/Icon';
import BrandIcon from 'sail/BrandIcon';
import {stripe} from 'sail/BrandIcon/Assets';
import {
  customer,
  cross,
  platform,
  business,
  bank,
  terminal,
} from 'sail/Icon/Assets';
import type {IconAsset} from 'sail/Icon/types';
import {TopHandles, BottomHandles, LeftHandles, RightHandles} from '../Handles';

import './OverviewDiagram.scss';

type OverviewDiagramIconNodeType =
  | 'customer'
  | 'client'
  | 'server'
  | 'platform'
  | 'stripe'
  | 'account'
  | 'terminal'
  | 'pos'
  | 'bank'
  | 'location'
  | 'finaccount';

const messages = defineMessages({
  customer: {
    id: 'docsShell.diagram.overviewDiagram.customer',
    description: 'A label for an icon representing a customer',
    defaultMessage: 'Customer',
  },
  platform: {
    id: 'docsShell.diagram.overviewDiagram.platform',
    description: 'A label for an icon representing a Connect platform',
    defaultMessage: 'Platform',
  },
  client: {
    id: 'docsShell.diagram.overviewDiagram.client',
    description:
      'A label for an icon representing a client/frontend component of a Stripe integration',
    defaultMessage: 'Client',
  },
  server: {
    id: 'docsShell.diagram.overviewDiagram.server',
    description:
      'A label for an icon representing a server/backend component of a Stripe integration',
    defaultMessage: 'Server',
  },
  bank: {
    id: 'docsShell.diagram.overviewDiagram.bank',
    description: 'A label for an icon representing a bank',
    defaultMessage: 'Bank',
  },
  account: {
    id: 'docsShell.diagram.overviewDiagram.account',
    description: 'A label for an icon representing a Connect Account on Stripe',
    defaultMessage: 'Connect account',
  },
  terminal: {
    id: 'docsShell.diagram.overviewDiagram.terminal',
    description: 'A label for an icon representing a Stripe Terminal reader',
    defaultMessage: 'Reader',
  },
  finaccount: {
    id: 'docsShell.diagram.overviewDiagram.finaccount',
    description: 'A label for an icon representing a Financial Account',
    defaultMessage: 'Financial account',
  },
});

/* TODO: Add remaining icons once they are designed */
export const ICONS: {[key: OverviewDiagramIconNodeType]: IconAsset} = {
  customer,
  platform,
  bank,
  terminal,
  account: business,
  finaccount: bank,
};

type OverviewDiagramIconNodeData = {|
  icon: OverviewDiagramIconNodeType,
|};

type OverviewDiagramIconNodeProps = {|
  data: OverviewDiagramIconNodeData,
|};

export const OverviewDiagramIconNode = ({
  data,
}: OverviewDiagramIconNodeProps) => {
  const {icon} = data;

  let IconComponent;
  let label;
  if (icon === 'stripe') {
    IconComponent = <BrandIcon icon={stripe} />;
    label = 'Stripe';
  } else if (['client', 'server'].includes(icon)) {
    IconComponent = <Box className={`OverviewDiagramIconNode-icon--${icon}`} />;
    label = <FormattedMessage {...messages[icon]} />;
  } else if (icon in ICONS) {
    IconComponent = <Icon icon={ICONS[icon]} color={'white'} />;
    label = <FormattedMessage {...messages[icon]} />;
  } else {
    // We do not have an icon for this yet
    IconComponent = <Icon icon={cross} color={'gray900'} />;
  }

  return (
    <Group direction="vertical" className="OverviewDiagramIconNode">
      <TopHandles />
      <BottomHandles />
      <Box
        flexBasis="100%"
        flex={{alignItems: 'center', direction: 'column'}}
        style={{position: 'relative'}}
        padding={8}
      >
        <LeftHandles />
        <Box
          className={classnames(
            'OverviewDiagramIconNode-icon',
            `OverviewDiagramIconNode-icon-${icon || 'neutral'}`,
          )}
          flex={{alignItems: 'center', justifyContent: 'center'}}
        >
          {IconComponent}
        </Box>
        <RightHandles />
      </Box>
      <Box padding={{horizontal: 8}} margin={{bottom: 8}}>
        <BodyAlt align="center" color={'gray900'}>
          {label}
        </BodyAlt>
      </Box>
    </Group>
  );
};
